<template>
  <validation-observer v-slot="{ handleSubmit, valid }">
    <form @submit.prevent="handleSubmit(onSubmit)">
      <v-row>
        <v-col>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            name="title"
          >
            <v-text-field
              label="Title"
              name="title"
              flat
              outlined
              :hide-details="false"
              :error="errors.length > 0"
              :error-count="errors.length"
              :error-messages="errors"
              v-model="formModel.group_name"
            />
          </validation-provider>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="text--secondary caption mb-1">
            Microsoft capabilities (optional Teams requires extra consent)
          </div>
          <v-checkbox
            v-model="selectedCapabilities"
            value="mail"
            label="Mail (read & send)"
            hide-details
            dense
          />
          <v-checkbox
            v-model="selectedCapabilities"
            value="drive"
            label="OneDrive & SharePoint files"
            hide-details
            dense
          />
          <v-checkbox
            v-model="selectedCapabilities"
            value="teams"
            label="Microsoft Teams"
            hide-details
            dense
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex justify-end">
          <v-btn plain class="mr-2" @click="onCancel" :disabled="loading">
            Cancel
          </v-btn>
          <v-btn
            type="submit"
            color="primary"
            :disabled="!valid || loading || !hasAnyCapability"
            :loading="performingOutlookAction"
          >
            Authorize Microsoft 365
          </v-btn>
        </v-col>
      </v-row>
    </form>
  </validation-observer>
</template>

<script>
import uuidv4 from '@/util/uuid_generator';
import {
  MICROSOFT_GRAPH_CAPABILITY,
  normalizeCapabilities,
  scopesForCapabilities,
} from '@/util/microsoftGraphScopes';
import { createNamespacedHelpers } from 'vuex';

const { mapActions: workflowsActions, mapGetters: workflowsGetters } =
  createNamespacedHelpers('workflows/list');
const { mapActions: credsActions } = createNamespacedHelpers('credentials');

const DEFAULT_CAPS = [
  MICROSOFT_GRAPH_CAPABILITY.MAIL,
  MICROSOFT_GRAPH_CAPABILITY.DRIVE,
];

export default {
  name: 'OutlookForm',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    credentialToEdit: {
      type: Object,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      code: null,
      formModel: {
        group_name: '',
      },
      selectedCapabilities: [...DEFAULT_CAPS],
    };
  },
  computed: {
    ...workflowsGetters({
      performingOutlookAction: 'PERFORM_OUTLOOK_ACTION',
    }),
    hasAnyCapability() {
      return Array.isArray(this.selectedCapabilities)
        ? this.selectedCapabilities.length > 0
        : false;
    },
  },
  methods: {
    ...workflowsActions(['outlookAction']),
    ...credsActions(['fetchAuthentications']),
    buildAuthorizeUrl() {
      const caps = normalizeCapabilities(this.selectedCapabilities);
      const scopeList = scopesForCapabilities(caps);
      const scope = `scope=${encodeURIComponent(scopeList.join(' '))}`;
      const clientId = `client_id=${process.env.VUE_APP_OUTLOOK_CLIENT_ID}`;
      const redirectURI = `redirect_uri=${encodeURIComponent(
        process.env.VUE_APP_OUTLOOK_REDIRECT_URI || '',
      )}`;
      const responseType = 'response_type=code';
      const accessType = 'access_type=offline';
      const responseMode = 'response_mode=query';
      const prompt = 'prompt=select_account';
      const state = uuidv4();
      localStorage.setItem('ou_state', state);
      const stateQs = `state=${encodeURIComponent(state)}`;
      const qs = `${clientId}&${scope}&${redirectURI}&${responseType}&${accessType}&${responseMode}&${prompt}&${stateQs}`;
      return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${qs}`;
    },
    async authorizeOutlook() {
      const requestURI = this.buildAuthorizeUrl();
      const options = 'height=500,width=500,status=yes';
      const popupWindow = window.open(requestURI, 'popUpWindow', options);
      const intervalId = setInterval(async () => {
        try {
          if (window.location.origin === popupWindow.location.origin) {
            const params = new URLSearchParams(popupWindow.location.search);
            const state = params.get('state');
            const savedState = localStorage.getItem('ou_state');

            if (state !== savedState) {
              this.$store.dispatch('notifications/setError', 'Invalid State', {
                root: true,
              });
              clearInterval(intervalId);
              popupWindow.close();
              return;
            }

            this.code = params.get('code');
            clearInterval(intervalId);
            popupWindow.close();

            await this.submitWithCode();
          }
        } catch (e) {
          // catch cross origin exception
        }
      }, 100);
    },
    async submitWithCode() {
      if (!this.code && this.isEdit) {
        this.$emit('submit-form', this.formModel);
      } else if (this.code) {
        const caps = normalizeCapabilities(this.selectedCapabilities);
        const data = {
          group_name: this.formModel.group_name,
          code: this.code,
          action: 'get_token',
          microsoftGraphCapabilities: caps,
        };
        if (this.isEdit) {
          data['authenticationId'] = this.credentialToEdit?.id;
        }

        try {
          const response = await this.outlookAction(data);
          if (response && response.success) {
            this.$store.dispatch(
              'notifications/pushNotification',
              'Successfully logged in to Microsoft 365',
              { root: true },
            );
            await this.fetchAuthentications();
            this.onCancel();
          } else {
            throw new Error('Failed to authenticate with Microsoft 365');
          }
        } catch (error) {
          this.$store.dispatch(
            'notifications/setError',
            error.message || 'Failed to authenticate with Microsoft 365',
            { root: true },
          );
          this.$emit('reopen');
        }
      }
    },
    async onSubmit() {
      await this.authorizeOutlook();
    },
    onCancel() {
      this.$emit('close');
    },
    parseStoredCapabilities(credentials) {
      if (!Array.isArray(credentials)) {
        return null;
      }
      const row = credentials.find((c) => c.name === 'graph_capabilities');
      if (!row?.value) {
        return null;
      }
      try {
        const parsed = JSON.parse(row.value);
        const caps = normalizeCapabilities(parsed);
        return caps.length ? caps : null;
      } catch {
        return null;
      }
    },
  },
  mounted() {
    if (this.isEdit) {
      const { name, credentials } = this.credentialToEdit;
      const stored = this.parseStoredCapabilities(credentials);
      this.formModel = {
        group_name: name,
        credentials_list: credentials.map((item) => ({
          ...item,
          is_disabled: item.is_encrypted,
        })),
      };
      this.selectedCapabilities = stored ? [...stored] : [...DEFAULT_CAPS];
    }
  },
};
</script>
